import { useEffect, useRef, useState } from 'react';

import { getApiV5FirmsContactsSuggestions } from '@liscio/api';

import { ContactSuggestion, isMeaningfulEmailQuery } from './contactSuggestions';

const EMPTY_SUGGESTIONS: ContactSuggestion[] = [];

export type UseContactSuggestionsByEmailOptions = {
  debounceMs?: number;
  enabled?: boolean;
};

export function useContactSuggestionsByEmail(
  emailQuery: string,
  options?: UseContactSuggestionsByEmailOptions
) {
  const requestSequenceRef = useRef(0);
  const [suggestions, setSuggestions] = useState<ContactSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const debounceMs = options?.debounceMs ?? 400;
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    const trimmedQuery = emailQuery.trim();
    const shouldFetch = enabled && isMeaningfulEmailQuery(trimmedQuery);

    if (!shouldFetch) {
      requestSequenceRef.current += 1;
      setSuggestions((current) => (current.length === 0 ? current : EMPTY_SUGGESTIONS));
      setIsLoading(false);
      setError(null);
      return;
    }

    const currentRequestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = currentRequestSequence;
    setIsLoading(true);
    setError(null);

    const timer = window.setTimeout(async () => {
      try {
        const data = await getApiV5FirmsContactsSuggestions({
          query: trimmedQuery,
          email: trimmedQuery,
        });

        if (requestSequenceRef.current !== currentRequestSequence) {
          return;
        }

        setSuggestions((data?.data ?? []) as ContactSuggestion[]);
      } catch (requestError) {
        if (requestSequenceRef.current !== currentRequestSequence) {
          return;
        }

        setSuggestions([]);
        setError(requestError);
      } finally {
        if (requestSequenceRef.current === currentRequestSequence) {
          setIsLoading(false);
        }
      }
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [debounceMs, emailQuery, enabled]);

  return {
    suggestions,
    isLoading,
    error,
  };
}
